import React from "react";
import { motion, type Transition } from "framer-motion";

const VARIANTS = {
  success: { bg: "#E3FABD", hover: "#D4E8B8", cross: "#8BA888" },
  warning: { bg: "#FFF0D1", hover: "#F1DCAE", cross: "#C9A24B" },
} as const;

export type StatusToastVariant = keyof typeof VARIANTS;

export type StatusToastProps = {
  /** success = green (confirmation), warning = yellow (degraded state). */
  variant: StatusToastVariant;
  /** Leading icon, sized by the caller (typically 20×20). */
  icon?: React.ReactNode;
  /**
   * Present = show the dismiss cross. The pill is pointer-transparent except
   * for this button, so content stays interactive underneath.
   */
  onDismiss?: () => void;
  dismissLabel?: string;
  /**
   * framer-motion transition override. Omit for framer's defaults (springy
   * y, 300ms fade) — the choreography the app's toasts have always had.
   */
  transition?: Transition;
  /** Toast text. */
  children: React.ReactNode;
  /** Extra classes for the outer wrapper (positioning, z-index, width). */
  className?: string;
};

/**
 * The status pill used for transient system state — connection changes, mode
 * toggles. One source for the palette and layout, so variants cannot drift
 * apart by copying hex codes between files.
 *
 * A thin `motion.div`: render it as a keyed child of the consumer's
 * `AnimatePresence` (typically `mode="wait"`), exactly like the hand-rolled
 * toasts it replaces. Mount/unmount, sequencing, and timers stay with the
 * caller.
 */
export const StatusToast = ({
  variant,
  icon,
  onDismiss,
  dismissLabel = "Dismiss",
  transition,
  children,
  className = "",
}: StatusToastProps) => {
  const v = VARIANTS[variant];
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={transition}
      role="status"
      aria-live="polite"
      className={`flex select-none justify-center pointer-events-none ${className}`}
    >
      <div
        className="flex items-center gap-3 rounded-[10px] px-5 py-[15px]"
        style={{ background: v.bg }}
      >
        {icon}
        <span className="text-base font-medium text-black">{children}</span>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="rounded-full p-1 transition-colors pointer-events-auto"
            style={{ color: v.cross }}
            onMouseEnter={(e) => (e.currentTarget.style.background = v.hover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = "")}
            aria-label={dismissLabel}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default StatusToast;
